import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import type {
	PromptHistoryFileType,
	RecurringCalculationResult,
	RecurringCalculationSettings,
	RecurringCreationResult,
	RecurringEmployeeHistory,
	RecurringStartPreview,
	SaveRecurringShiftHoursRequest,
	SaveRecurringShiftHoursResult,
	ShiftHoursSaveSkipReason,
} from "../../services/backend.service";
import { backendService } from "../../services/backend.service";
import type { ExternalSource } from "../../utils/models";
import type { SuspectRecord } from "../../utils/nurseMergeModels";
import { withSavedJobShiftTimes } from "./sections/CalculateRecurring/recurringDisplay";
import type { ConflictDecision, LoadRecurringResult, LoadShiftsResult, ResolveConflictsResult } from "./sections/LoadResultDisplay";

const DATA_LOAD_VALIDATION_SUMMARY_QUERY_KEY = "ops-backoffice-data-load-summary";

// Re-export so consumers (e.g. the Data Load tab) keep importing this type from hooks.
export type { PromptHistoryFileType };

export type PromptHistoryRow = {
	id: string;
	department_id: string;
	file_name: string;
	file_type: PromptHistoryFileType;
	prompt: string;
	tab_instructions: Record<string, string>;
	s3_bucket: string | null;
	s3_key: string | null;
	load: boolean;
	loaded_at: string | null;
	created_at: string;
	created_by: string | null;
};

export function useRecurringStartPreview(departmentId: string | undefined, minStartDate: string | undefined, enabled: boolean) {
	return useQuery<RecurringStartPreview | null>({
		queryKey: ["onboarding_tools", "recurring_start_preview", departmentId, minStartDate],
		queryFn: async () => {
			if (!departmentId) return null;
			const response = await backendService.getRecurringStartPreview({ departmentId, ...(minStartDate ? { minStartDate } : {}) });
			return response.data;
		},
		enabled: enabled && Boolean(departmentId),
		staleTime: 0,
	});
}

export function useCalculateRecurringShifts() {
	const { showErrorSnackbar } = useHospitalData();

	return useMutation({
		mutationFn: async (departmentId: string): Promise<ArrayBuffer> => {
			const response = await backendService.calculateRecurringShifts(departmentId);
			return response.data;
		},
		onError: (error, departmentId) => {
			const axiosError = error as AxiosError<{ detail?: string }>;
			const errorMessage = axiosError.response?.data?.detail || "Failed to calculate recurring shifts";
			showErrorSnackbar(errorMessage, { department_id: departmentId }, error);
		},
	});
}

/**
 * One employee's real history for the review calendar (DAT-774).
 *
 * A query, not a mutation: it reads nothing but the schedule, so it is safe to cache and re-serve
 * while the reviewer flips between employees. Disabled until a calendar is actually open, which is
 * what keeps a department's worth of history off the wire.
 */
export function useRecurringEmployeeHistory(departmentId: string | undefined, nurseId: number | undefined, windowMonths: number) {
	return useQuery<RecurringEmployeeHistory | null>({
		queryKey: ["onboarding_tools", "recurring_employee_history", departmentId, nurseId, windowMonths],
		queryFn: async () => {
			if (!departmentId || nurseId === undefined) return null;
			const response = await backendService.getRecurringEmployeeHistory({ departmentId, nurseId, windowMonths });
			return response.data;
		},
		enabled: Boolean(departmentId) && nurseId !== undefined,
		staleTime: 60_000,
	});
}

/**
 * Propose patterns for review (DAT-774). A mutation rather than a query: it is a deliberate,
 * expensive run the operator triggers with chosen settings, not something to refetch on focus.
 */
export function useCalculateRecurringCandidates() {
	const { showErrorSnackbar } = useHospitalData();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async (params: { departmentId: string; settings: RecurringCalculationSettings }): Promise<RecurringCalculationResult> => {
			const response = await backendService.calculateRecurringCandidates(params);
			return response.data;
		},
		// The settings are on the start event because they are the whole question this flow asks: a
		// department whose runs are all at minimum_score 0.4 is one where the fit is not working, and
		// that is invisible if only the outcome is recorded.
		onMutate: (params) =>
			track("recurring_calculate_start", {
				department_id: params.departmentId,
				minimum_score: params.settings.minimumScore,
				pattern: params.settings.pattern,
				base_window_months: params.settings.baseWindowMonths,
				confirmed_job_count: params.settings.shiftHours.length,
			}),
		onSuccess: (result, params) =>
			track("recurring_calculate_success", {
				department_id: params.departmentId,
				employees: result.headline.employees,
				ready: result.headline.ready,
				needs_review: result.headline.needsReview,
				skipped: result.headline.skipped,
				accuracy: result.headline.accuracy,
			}),
		onError: (error, params) => {
			const axiosError = error as AxiosError<{ detail?: string }>;
			const errorMessage = axiosError.response?.data?.detail || "Failed to calculate recurring patterns";
			trackError("recurring_calculate_error", { department_id: params.departmentId, error_message: errorMessage });
			showErrorSnackbar(errorMessage, { department_id: params.departmentId }, error);
		},
	});
}

/** Create templates for the approved candidates (DAT-774) — the only call in this flow that writes. */
export function useApproveRecurringCandidates() {
	const { showErrorSnackbar } = useHospitalData();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async (params: {
			departmentId: string;
			settings: RecurringCalculationSettings;
			approvedCandidateIds: string[];
		}): Promise<RecurringCreationResult> => {
			const response = await backendService.approveRecurringCandidates(params);
			return response.data;
		},
		onMutate: (params) => track("recurring_approve_start", { department_id: params.departmentId, approved_count: params.approvedCandidateIds.length }),
		// A partial success is the outcome worth separating: templates were created AND something was
		// refused, which a single success event would report as a clean win.
		onSuccess: (result, params) =>
			track("recurring_approve_success", {
				department_id: params.departmentId,
				approved_count: result.approvedCount,
				created_pattern_count: result.createdPatternCount,
				created_employee_count: result.createdEmployeeCount,
				already_existing_pattern_count: result.alreadyExistingPatternCount,
				failed_shift_count: result.failedShifts.length,
				unmapped_role_count: result.unmappedRoles.length,
				partial: result.failedShifts.length > 0 || result.unmappedRoles.length > 0,
			}),
		onError: (error, params) => {
			const axiosError = error as AxiosError<{ detail?: string }>;
			const errorMessage = axiosError.response?.data?.detail || "Failed to create recurring shifts";
			trackError("recurring_approve_error", {
				department_id: params.departmentId,
				approved_count: params.approvedCandidateIds.length,
				error_message: errorMessage,
			});
			showErrorSnackbar(errorMessage, { department_id: params.departmentId }, error);
		},
	});
}

/** Analytics property per skip reason — snake_case, unlike the wire values. */
const SKIP_REASON_ANALYTICS_KEYS: Record<ShiftHoursSaveSkipReason, string> = {
	alreadyConfigured: "skipped_already_configured_count",
	jobNotEnabled: "skipped_job_not_enabled_count",
	unknownJob: "skipped_unknown_job_count",
};

/** How many skipped jobs each reason accounts for, as analytics properties. */
function skipReasonCounts(result: SaveRecurringShiftHoursResult): Record<string, number> {
	return Object.fromEntries(
		Object.entries(SKIP_REASON_ANALYTICS_KEYS).map(([reason, key]) => [key, result.skipped.filter((entry) => entry.reason === reason).length]),
	);
}

type SaveRecurringShiftHoursParams = { departmentId: string; confirmedCount: number; explicitlySelectedCount: number } & SaveRecurringShiftHoursRequest;

/**
 * Store the hours reviewed in Calculate Recurring as new per-job rows in Department Configuration.
 *
 * On success the chosen department's `jobShiftTimes` is replaced with the server's post-save table, so
 * Settings and every shift-time picker see the new rows without a reload — the same in-memory
 * department they already read from.
 */
export function useSaveRecurringShiftHours() {
	const { t } = useTranslation();
	const { showErrorSnackbar, setChosenDepartment } = useHospitalData();
	const { updateUserInfo } = useAuth();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async ({ departmentId, jobs }: SaveRecurringShiftHoursParams): Promise<SaveRecurringShiftHoursResult> => {
			const response = await backendService.saveRecurringShiftHours({ departmentId, jobs });
			return response.data;
		},
		onMutate: (params) =>
			track("recurring_shift_hours_save_start", {
				department_id: params.departmentId,
				job_count: params.jobs.length,
				confirmed_count: params.confirmedCount,
				explicitly_selected_count: params.explicitlySelectedCount,
			}),
		onSuccess: (result, params) => {
			track("recurring_shift_hours_save_success", {
				department_id: params.departmentId,
				saved_count: result.saved.length,
				skipped_count: result.skipped.length,
				...skipReasonCounts(result),
			});
			setChosenDepartment((prev) => withSavedJobShiftTimes(prev, params.departmentId, result.jobShiftTimes));
			// The chosen department is rebuilt from userInfo.departments on every department switch, and
			// a Settings save replaces the whole per-job table with what the form loaded — so a stale
			// userInfo would bring the pre-save table back and delete these rows. Refetched the same way
			// the Settings save refreshes it; updateUserInfo reports its own failure.
			void updateUserInfo();
		},
		onError: (error, params) => {
			const axiosError = error as AxiosError<{ detail?: string }>;
			trackError("recurring_shift_hours_save_error", {
				department_id: params.departmentId,
				job_count: params.jobs.length,
				error_message: axiosError.message,
				error_detail: axiosError.response?.data?.detail,
			});
			// A UIVisibleError detail is translated by showErrorSnackbar; this is the message for anything else.
			showErrorSnackbar(t("onboarding_load.recurring_shift_hours_save_failed"), { department_id: params.departmentId }, error);
		},
	});
}

type LoadScheduleShiftsParams = {
	departmentId: string;
	file: File;
	source?: ExternalSource;
	allowDuplicateNurseMatches?: boolean;
	promptHistoryId?: string;
};

type LoadScheduleRecurringShiftsParams = {
	departmentId: string;
	file: File;
	minStartDate?: string;
	allowDuplicateNurseMatches?: boolean;
	promptHistoryId?: string;
};

export function useLoadScheduleShifts() {
	const { showErrorSnackbar } = useHospitalData();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (params: LoadScheduleShiftsParams): Promise<LoadShiftsResult> => {
			const response = await backendService.loadScheduleShifts({
				departmentId: params.departmentId,
				file: params.file,
				...(params.source ? { source: params.source } : {}),
				allowDuplicateNurseMatches: params.allowDuplicateNurseMatches,
				...(params.promptHistoryId ? { promptHistoryId: params.promptHistoryId } : {}),
			});
			return response.data;
		},
		onSuccess: (_data, params) => {
			void queryClient.invalidateQueries({ queryKey: [DATA_LOAD_VALIDATION_SUMMARY_QUERY_KEY, params.departmentId] });
		},
		onError: (error, params) => {
			const axiosError = error as AxiosError<{ detail?: string }>;
			const errorMessage = axiosError.response?.data?.detail || "Failed to load shifts";
			showErrorSnackbar(errorMessage, { department_id: params.departmentId }, error);
		},
	});
}

export function useLoadScheduleRecurringShifts() {
	const { showErrorSnackbar } = useHospitalData();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (params: LoadScheduleRecurringShiftsParams): Promise<LoadRecurringResult> => {
			const response = await backendService.loadScheduleRecurringShifts({
				departmentId: params.departmentId,
				file: params.file,
				...(params.minStartDate ? { minStartDate: params.minStartDate } : {}),
				allowDuplicateNurseMatches: params.allowDuplicateNurseMatches,
				...(params.promptHistoryId ? { promptHistoryId: params.promptHistoryId } : {}),
			});
			return response.data;
		},
		onSuccess: (_data, params) => {
			void queryClient.invalidateQueries({ queryKey: [DATA_LOAD_VALIDATION_SUMMARY_QUERY_KEY, params.departmentId] });
		},
		onError: (error, params) => {
			const axiosError = error as AxiosError<{ detail?: string }>;
			const errorMessage = axiosError.response?.data?.detail || "Failed to load recurring shifts";
			showErrorSnackbar(errorMessage, { department_id: params.departmentId }, error);
		},
	});
}

export function useResolveShiftConflicts() {
	const { showErrorSnackbar } = useHospitalData();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: async (params: { departmentId: string; decisions: ConflictDecision[] }): Promise<ResolveConflictsResult> => {
			const response = await backendService.resolveShiftConflicts(params);
			return response.data;
		},
		onError: (error, params) => {
			const axiosError = error as AxiosError<{ detail?: string }>;
			const errorMessage = axiosError.response?.data?.detail || t("onboarding_load.conflicts.resolve_failed");
			showErrorSnackbar(errorMessage, { department_id: params.departmentId }, error);
		},
	});
}

// Fills the form once per key change (e.g. when the user picks a different department):
// wait for loading to finish, then call onHydrate with the server data, or onFallback if there's none.
export function useHydrateFromHistory<T>(params: {
	key: string;
	isLoading: boolean;
	serverData: T | null | undefined;
	onHydrate: (data: T) => void;
	onFallback: () => void;
}): void {
	const { key, isLoading, serverData, onHydrate, onFallback } = params;
	const onHydrateRef = useRef(onHydrate);
	const onFallbackRef = useRef(onFallback);
	onHydrateRef.current = onHydrate;
	onFallbackRef.current = onFallback;

	const hydratedKey = useRef<string | null>(null);
	useEffect(() => {
		hydratedKey.current = null;
	}, [key]);
	useEffect(() => {
		if (hydratedKey.current === key) return;
		if (isLoading) return;
		if (serverData) {
			onHydrateRef.current(serverData);
		} else {
			onFallbackRef.current();
		}
		hydratedKey.current = key;
	}, [key, isLoading, serverData]);
}

export function useLatestPromptHistory(fileType: PromptHistoryFileType) {
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment?.department_id;
	const enabled = Boolean(departmentId);
	return useQuery<PromptHistoryRow | null>({
		queryKey: ["onboarding_tools", "prompt_history_latest", departmentId, fileType],
		queryFn: async () => {
			if (!departmentId) return null;
			try {
				const response = await backendService.getLatestPromptHistory(departmentId, fileType);
				return response.data;
			} catch (error) {
				const axiosError = error as AxiosError;
				if (axiosError.response?.status === 404) return null;
				throw error;
			}
		},
		enabled,
		staleTime: 0,
	});
}

export type FailedTab = {
	name: string;
	error: string;
};

type UpdateFirstSundayParams = {
	departmentId: string;
	firstSunday: string;
};

export function useUpdateDepartmentFirstSunday() {
	const { showErrorSnackbar } = useHospitalData();

	return useMutation({
		mutationFn: async (params: UpdateFirstSundayParams): Promise<void> => {
			await backendService.updateDepartmentFirstSunday(params.departmentId, params.firstSunday);
		},
		onError: (error, params) => {
			const axiosError = error as AxiosError<{ detail?: string }>;
			// DEFAULT: the backend detail when it sends one, so the operator sees the real reason; this
			// literal is only the fallback for a network/opaque failure. Worded without a weekday to
			// match the dialog that triggers it — the column is `first_sunday`, but the operator may be
			// looking at a non-Sunday week (DAT-832).
			const errorMessage = axiosError.response?.data?.detail || "Failed to update week numbering";
			showErrorSnackbar(errorMessage, { department_id: params.departmentId }, error);
		},
	});
}

export function useGetSheetNames() {
	const { showErrorSnackbar } = useHospitalData();

	return useMutation({
		mutationFn: async (file: File): Promise<string[]> => {
			const response = await backendService.getSheetNames(file);
			return response.data.sheet_names;
		},
		onError: (error) => {
			const axiosError = error as AxiosError<{ detail?: string }>;
			const errorMessage = axiosError.response?.data?.detail || "Failed to read sheet names";
			showErrorSnackbar(errorMessage, {}, error);
		},
	});
}

type CleanScheduleParams = {
	departmentId: string;
	deleteFromDate?: string;
	cleanScheduleTemplate?: boolean;
};

export function useCleanSchedule() {
	const { showErrorSnackbar } = useHospitalData();

	return useMutation({
		mutationFn: async (params: CleanScheduleParams): Promise<void> => {
			await backendService.cleanSchedule({
				departmentId: params.departmentId,
				...(params.deleteFromDate ? { deleteFromDate: params.deleteFromDate } : {}),
				...(params.cleanScheduleTemplate ? { cleanScheduleTemplate: params.cleanScheduleTemplate } : {}),
			});
		},
		onError: (error, params) => {
			const axiosError = error as AxiosError<{ detail?: string }>;
			const errorMessage = axiosError.response?.data?.detail || "Failed to clean schedule";
			showErrorSnackbar(errorMessage, { department_id: params.departmentId }, error);
		},
	});
}

type LoadNewNursesParams = {
	departmentId: string;
	file: File;
	promptHistoryId?: string;
};

/** `held`: not created — same name as a record elsewhere in the network; waits for the onboarder (neither success nor failure). */
export type NurseAction = "created" | "linked" | "held" | "updated" | "unchanged" | "failed";

/** One spreadsheet cell of a held row, as the loader received it. */
export type HeldCellValue = string | number | boolean | null;

export type NurseFieldChange = {
	old: unknown;
	new: unknown;
};

/** Which uploaded value points at the person's other record: their email, their phone, or (for two records in one department) their name. */
export type RosterMatchedOn = "email" | "phone_number" | "name";

/**
 * Why a record is the same person's: `contact` — the row's email/phone is already on it; `same_name` — another
 * record here with the row's name; `linked` — the record a created row was folded into (this facility added).
 */
export type FloatPoolMatchKind = "contact" | "same_name" | "linked";

/** Records on a result row are Duplicate employees records (camelCase), unlike the row's own snake_case keys. */
export type FloatPoolMatch = SuspectRecord & {
	kind: FloatPoolMatchKind;
	matchedOn: RosterMatchedOn[];
};

/** One row of a roster load's results. Rows that failed before the name was read carry no name. */
export type NurseResult = {
	first_name?: string;
	last_name?: string;
	success: boolean | string;
	reason: string;
	nurse_id?: number;
	email?: string;
	phone_number?: string;
	role?: string;
	action?: NurseAction;
	changes?: Record<string, NurseFieldChange>;
	skipped_fields?: { email?: string; phone_number?: string };
	/** The person's other records: the one holding the uploaded contact, every other same-name record here, or (linked) the record this facility was added to. */
	float_pool_matches?: FloatPoolMatch[];
	/** The record the row wrote to, as stored — present whenever `float_pool_matches` is, except on a linked row. Departments in stored order: the first is its home. */
	record_here?: SuspectRecord;
	/** Same-name records in this department that the row could not choose between. */
	same_name_records?: SuspectRecord[];
	/** Held rows only: the records elsewhere in the network carrying exactly this name. */
	held_candidates?: SuspectRecord[];
	/** Held rows only: the row's cells as uploaded, sent back as-is to create it as a separate record. */
	held_row?: Record<string, HeldCellValue>;
};

type DuplicateOccurrence = {
	index: number;
	name: string;
	original?: string;
};

type DuplicateDetail = {
	phone?: string;
	email?: string;
	occurrences: DuplicateOccurrence[];
	total_count: number;
};

type LoadNewNursesResponse = {
	results: NurseResult[];
	summary: {
		total: number;
		success: number;
		held: number;
		failed: number;
	};
	is_update: boolean;
	duplicate_cleanup?: {
		duplicate_phones_cleaned: number;
		duplicate_emails_cleaned: number;
		phone_details: DuplicateDetail[];
		email_details: DuplicateDetail[];
	};
};

export function useGenerateOnboardingReport() {
	const { showErrorSnackbar } = useHospitalData();

	return useMutation({
		mutationFn: async (departmentId: string): Promise<ArrayBuffer> => {
			const response = await backendService.generateOnboardingReport(departmentId);
			return response.data;
		},
		onError: (error, departmentId) => {
			const axiosError = error as AxiosError<{ detail?: string }>;
			const errorMessage = axiosError.response?.data?.detail || "Failed to generate onboarding report";
			showErrorSnackbar(errorMessage, { department_id: departmentId }, error);
		},
	});
}

export function useLoadNewNurses() {
	const { showErrorSnackbar } = useHospitalData();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (params: LoadNewNursesParams): Promise<LoadNewNursesResponse> => {
			const response = await backendService.loadNewNurses({
				departmentId: params.departmentId,
				file: params.file,
				...(params.promptHistoryId ? { promptHistoryId: params.promptHistoryId } : {}),
			});
			return response.data;
		},
		onSuccess: (_data, params) => {
			void queryClient.invalidateQueries({ queryKey: [DATA_LOAD_VALIDATION_SUMMARY_QUERY_KEY, params.departmentId] });
		},
		onError: (error, params) => {
			const axiosError = error as AxiosError<{ detail?: string }>;
			const errorMessage = axiosError.response?.data?.detail || "Failed to load nurses";
			showErrorSnackbar(errorMessage, { department_id: params.departmentId }, error);
		},
	});
}

export function useUpdateExistingNurses() {
	const { showErrorSnackbar } = useHospitalData();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (params: LoadNewNursesParams): Promise<LoadNewNursesResponse> => {
			const response = await backendService.updateExistingNurses({
				departmentId: params.departmentId,
				file: params.file,
				...(params.promptHistoryId ? { promptHistoryId: params.promptHistoryId } : {}),
			});
			return response.data;
		},
		onSuccess: (_data, params) => {
			void queryClient.invalidateQueries({ queryKey: [DATA_LOAD_VALIDATION_SUMMARY_QUERY_KEY, params.departmentId] });
		},
		onError: (error, params) => {
			const axiosError = error as AxiosError<{ detail?: string }>;
			const errorMessage = axiosError.response?.data?.detail || "Failed to update nurses";
			showErrorSnackbar(errorMessage, { department_id: params.departmentId }, error);
		},
	});
}
